import React, { useEffect, useState } from 'react'
import { ScrollView, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { Button, Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { OutlinedIcon } from '@src/components'
import { useDispatchAsync } from '@src/utils'
import { Bugsnag } from '@src/config'
import { AppStackParamList, RootStackParamList } from '@src/navigation/types'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { LoadingContainer } from '../Common/containers/LoadingContainer'
// eslint-disable-next-line max-len
import { PreviewUserConversationMessageMutation } from '../NutritionistHub/graphql/previewUserConversationMessage.generated'

export const SendMessageToCoach = () => {
  useSecureScreen()

  const dispatchAsync = useDispatchAsync()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const styles = useStyleSheet(themedStyles)

  const route = useRoute<RouteProp<AppStackParamList, 'SendMessageToCoach'>>()
  const { messageTemplateKind } = route.params

  const [loading, setLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const sendMessage = async () => {
      setLoading(true)

      try {
        const response = await dispatchAsync<
          PreviewUserConversationMessageMutation['previewUserConversationMessage']
        >({
          type: 'users/previewMessage',
          payload: {
            kind: messageTemplateKind,
          },
        })

        if (!response.value) {
          throw new Error('Preview message response is empty')
        }

        await dispatchAsync({
          type: 'app/sendIntercomUserMessage',
          payload: {
            body: response.value,
          },
        })
      } catch (error) {
        setHasError(true)
        Bugsnag.notify(error as string)
      } finally {
        setLoading(false)
      }
    }

    sendMessage()
  }, [dispatchAsync, messageTemplateKind])

  const handleOnBack = () => {
    navigation.replace('Drawer', { screen: 'Dashboard' })
  }

  if (loading) {
    return <LoadingContainer />
  }

  return (
    <View style={styles.topLevelContainer}>
      <ScrollView
        bounces={false}
        contentContainerStyle={[styles.container, { paddingBottom: insets.bottom }]}
      >
        <View style={styles.content}>
          {hasError ? (
            <>
              <OutlinedIcon iconName="warning" iconStyle={styles.iconError} />
              <Text type="title-3" style={styles.title}>
                Something went wrong
              </Text>
              <Text type="regular" style={styles.subTitle}>
                Your message could not be sent. Please do not hesitate to contact your nutritionist
                via the chat
              </Text>
            </>
          ) : (
            <>
              <OutlinedIcon iconName="seal-check" iconStyle={styles.icon} />
              <Text type="title-3" style={styles.title}>
                Message sent successfully!
              </Text>
              <Text type="regular" style={styles.subTitle}>
                Your nutritionist has received the message and will get in contact with you as soon
                as possible.
              </Text>
            </>
          )}
        </View>
        <Button
          type="primary"
          size="block"
          onPress={handleOnBack}
          accessibilityLabel="Back to Nutritionist Hub"
          style={styles.button}
        >
          Back to Nutritionist Hub
        </Button>
      </ScrollView>
    </View>
  )
}

const themedStyles = StyleService.create({
  topLevelContainer: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  container: {
    paddingHorizontal: 16,
    paddingTop: 60,
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  content: {
    alignItems: 'center',
  },
  icon: {
    color: 'theme.success.base',
  },
  iconError: {
    color: 'theme.error.base',
  },
  title: {
    marginBottom: 16,
    textAlign: 'center',
    maxWidth: 280,
  },
  subTitle: {
    color: 'theme.text.primary',
    textAlign: 'center',
    marginBottom: 32,
  },
  button: {
    marginBottom: 8,
  },
})
